import React, { useState, useEffect } from "react";
import { Button, Input, Form, FormGroup, Card } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { reviewerSearchApi } from "src/network/api";
import { getCountriesApi } from "src/network/api";
import Pagination from "./Pagination";
import { STATIC } from "src/network/networkConfig";
import CountrySelector from "./CountrySelector";
import { DEPARTMENT } from "./DepartmentList";

const ReviewerCard = ({ searchQuery }) => {
  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    country: "",
    department: "",
    name: "",
  });
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [countries, setCountries] = useState([]);
  const limit = 10;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Fetch reviewers from API
  const fetchReviewers = async (currentPage: number) => {
    const offset = currentPage;
    setLoading(true);
    setError("");
    try {
      const response = await reviewerSearchApi({
        limit,
        offset,
        country: filters.country || "",
        department: filters.department || "",
        name: searchQuery,
      });
      console.log("API Response:", response);

      if (response.data?.status === 200) {
        setReviewers(response.data.data);
        const totalRecords = response.data.total || 0;
        setTotalPages(response.data.totalPages);
      }
    } catch (err) {
      setError("Failed to fetch reviewers. Please try again later.");
      console.error("Error fetching reviewers:", err);
      setReviewers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (countryId) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      country: countryId,
    }));
  };

  useEffect(() => {
    fetchReviewers(page);
  }, [page]);
  useEffect(() => {
    if (page == 1) {
      fetchReviewers(page);
    } else {
      setPage(1);
    }
  }, [filters, searchQuery, countryId]);

  const handleNavigate = (reviewerId: any) => {
    navigate(`/reviewer-profile/${reviewerId}`);
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row align-items-start">
        {/* Filter Section */}
        <div className="col-md-3 mb-4">
          <div
            style={{
              padding: "20px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "5px 5px 10px #d1d1d4, -5px -5px 10px #ffffff",
            }}
          >
            <h5 style={{ color: "#333", fontWeight: "bold" }}>
              Filter Reviewers
            </h5>
            <Form>
              <FormGroup>
                <CountrySelector
                  currentCountry={filters.country}
                  onSelectCountryCallback={handleCountrySelect}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  type="select"
                  name="department"
                  id="department"
                  value={filters.department}
                  onChange={handleFilterChange}
                  style={{
                    borderRadius: "8px",
                    padding: "10px",
                    border: "1px solid #ddd",
                    width: "100%",
                  }}
                >
                  <option value="">All Departments</option>
                  {DEPARTMENT.map((dept, index) => (
                    <option key={index} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <Button
                color="primary"
                onClick={() =>
                  setFilters({
                    ...filters,
                    country: "",
                    department: "",
                    name: "",
                  })
                }
                style={{
                  borderRadius: "8px",
                  // background: "#fca5a5",
                  border: "none",
                  padding: "5px 10px",
                  fontWeight: "bold",
                  width: "100%",
                  marginTop: "10px",
                  boxShadow: "2px 2px 5px #aaa, -2px -2px 5px #fff",
                }}
              >
                Reset Filters
              </Button>
            </Form>
          </div>
        </div>

        {/* Cards Section */}
        <div className="col-md-9">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height: "50vh", // Ensures the container takes full viewport height
              }}
            >
              <div
                className="alert alert-primary text-center fw-bold"
                style={{
                  fontSize: "1.1rem",
                  padding: "10px 20px",
                  borderRadius: "8px",
                }}
              >
                <i className="fas fa-spinner fa-spin me-2"></i> Loading, please
                wait...
              </div>
            </div>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : reviewers.length > 0 ? (
            reviewers.map((reviewer, index) => (
              <div
                key={index}
                className="container mb-3 reviewer-card"
                style={{
                  cursor: "pointer",
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "5px 5px 10px #d1d1d4, -5px -5px 10px #ffffff",
                  padding: "20px",
                  position: "relative",
                }}
                onClick={() => handleNavigate(reviewer.id)}
              >
                <div className="row align-items-center">
                  {/* Profile Image Section */}
                  <div className="col-12 col-md-3 text-center mb-3 mb-md-0">
                    <img
                      src={reviewer.profile_image || STATIC.noImageURL}
                      alt="Profile"
                      className="rounded-circle img-fluid border"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderWidth: "3px",
                        borderColor: "#e0e0e0",
                      }}
                    />
                  </div>
                  {/* Details Section */}
                  <div className="col-12 col-md-9">
                    {/* Name and Verified Badge */}
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-column flex-md-row">
                      <div>
                        <h2 className="h5 text-dark fw-bold mb-1">
                          {reviewer.name || "Unknown Name"}
                        </h2>
                      </div>
                      {reviewer.approval_status === "approved" && (
                        <div
                          style={{
                            backgroundColor: "#28a745",
                            color: "white",
                            fontWeight: "600",
                            padding: "6px 14px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            textTransform: "uppercase",
                          }}
                        >
                          Verified
                        </div>
                      )}
                    </div>

                    {/* Tag Information */}
                    <div className="d-flex flex-wrap gap-3 mt-3">
                      {[
                        {
                          label: "Designation",
                          value: reviewer.designation || "N/A",
                        },
                        {
                          label: "Qualification",
                          value: reviewer.maximum_qualification || "N/A",
                        },
                        {
                          label: "University",
                          value: reviewer.university_name || "N/A",
                        },
                        {
                          label: "Country",
                          value: reviewer.country_name || "N/A",
                        },
                        {
                          label: "Area",
                          value: reviewer.research_area || "N/A",
                        },
                        {
                          label: "Department/College",
                          value: reviewer.department || "Not Specified",
                        },
                      ].map((tag, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 rounded-pill pillls"
                          style={{
                            backgroundColor: "white",
                            color: "#0d6efd",
                            fontSize: "14px",
                            fontWeight: "500",
                            margin: "4px",
                            border: "1px solid #5E72E4",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <span
                            style={{ fontWeight: "400", color: "#495057" }}
                            className="span-text"
                          >
                            {tag.label}:
                          </span>{" "}
                          <strong className="text-dark">{tag.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height: "50vh",
              }}
            >
              <div
                className="alert alert-danger text-center fw-bold"
                style={{
                  fontSize: "1.1rem",
                  padding: "5px 10px",
                  borderRadius: "8px",
                  maxWidth: "400px",
                }}
              >
                <i className="fas fa-exclamation-circle me-2"></i> No reviewer
                found. Try adjusting your filters.
              </div>
            </div>
          )}
        </div>
        {reviewers.length > 0 && (
          <div className="text-center w-100">
            <Pagination total={totalPages} page={page} setPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewerCard;
